import { createSelector } from '@reduxjs/toolkit';

import type { AssetsSegmentSchema } from '@iconik-io/iconik-api/src/services/assets';
import { FLAG_SIMPLIFIED_ACLS } from '@iconik-io/shared-lib/src/analytics/flags';
import { growthbook } from '@iconik-io/shared-lib/src/analytics/growthbook';
import type { AssetEntityNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/asset';

import {
  ORDER_DATE_CREATED,
  ORDER_TIME_START_MILLISECONDS,
} from '../../constants/data';
import { SHARE_ASSET_PAGE_ROUTE } from '../../constants/routes';
import type { AppState } from '../../store/types';
import type { MentionsMode } from '../../ui/mention_input/mention_input';
import type { IdsMap } from '../asset_time_based_metadata/types';
import { selectCurrentVersionId } from '../version/selectors';

function getChildrenIds(id: string, relations: IdsMap): string[] {
  let childrenRecursively: string[] = [];
  if (relations[id]) {
    relations[id].forEach((childId) => {
      childrenRecursively.push(childId);
      childrenRecursively = childrenRecursively.concat(
        getChildrenIds(childId, relations),
      );
    });
  }

  return childrenRecursively;
}

const getAssetCommentsIds = (state: AppState, assetId: string): string[] =>
  state.assetCommentsReducer.segmentIds[assetId];

const getAsset = (
  state: AppState,
  assetId: string,
): AssetEntityNormalized | undefined => state.entities.assets[assetId];

const getAssetCommentsRelations = (state: AppState): IdsMap =>
  state.assetCommentsReducer.relations;

const getColorsFilter = (
  _state: AppState,
  _assetId: string,
  _versionId: string | undefined,
  colorsFilter?: string[],
) => colorsFilter;

const getTextFilter = (
  _state: AppState,
  _assetId: string,
  _versionId: string | undefined,
  _colorsFilter?: string[],
  textFilter?: string,
) => textFilter;

const getVersionIdFilter = (
  _state: AppState,
  _assetId: string,
  _versionId: string | undefined,
  _colorsFilter?: string[],
  _textFilter?: string,
  versionIdFilter?: string,
) => versionIdFilter;

const getSortField = (
  _state: AppState,
  _assetId: string,
  _versionId: string | undefined,
  _colorsFilter?: string[],
  _textFilter?: string,
  _versionIdFilter?: string,
  sortField?: keyof AssetsSegmentSchema,
) => sortField;

const getSegmentEntities = (state: AppState) => state.entities.segments;

export const selectAssetCommentIds = createSelector(
  [
    getAsset,
    getAssetCommentsIds,
    getSegmentEntities,
    getAssetCommentsRelations,
    selectCurrentVersionId,
    getColorsFilter,
    getTextFilter,
    getVersionIdFilter,
    getSortField,
  ],
  (
    asset,
    ids = [],
    segmentEntities,
    relations,
    currentVersionId,
    colorsFilter = [],
    textFilter,
    versionIdFilter,
    sortField = ORDER_TIME_START_MILLISECONDS,
  ) => {
    let assetCommentsFlatIds = [];

    if (ids.length === 0) {
      return [];
    }

    let rootCommentIds = ids.filter((id) => !segmentEntities[id].parent_id);
    if (colorsFilter.length > 0) {
      rootCommentIds = ids.filter((id) => {
        const comment = segmentEntities[id];
        if (!comment || !comment.segment_color) {
          return false;
        }
        return colorsFilter.includes(comment.segment_color);
      });
    }

    if (sortField && sortField !== ORDER_DATE_CREATED) {
      rootCommentIds = rootCommentIds.slice().sort((a, b) => {
        if (
          segmentEntities[a][sortField] === null &&
          segmentEntities[b][sortField] === null
        ) {
          return 0;
        }

        if (segmentEntities[a][sortField] === null) {
          return 1;
        }

        if (segmentEntities[b][sortField] === null) {
          return -1;
        }

        return (
          Number(segmentEntities[a][sortField]) -
          Number(segmentEntities[b][sortField])
        );
      });
    }

    const versionIdToFilter = versionIdFilter ?? currentVersionId;
    const versions = asset?.versions ?? [];
    if (versionIdFilter !== 'all' && versions.length > 1) {
      rootCommentIds = rootCommentIds.filter(
        (id) => segmentEntities[id].version_id === versionIdToFilter,
      );
    }

    assetCommentsFlatIds = rootCommentIds.reduce<string[]>(
      (accumulator, id) => {
        accumulator.push(id);
        return accumulator.concat(getChildrenIds(id, relations));
      },
      [],
    );

    assetCommentsFlatIds = assetCommentsFlatIds.filter((id) => {
      const comment = segmentEntities[id];
      if (comment && comment.status === 'DELETED') {
        // Only show an entry for the deleted comment if it has any non-deleted children
        const commentRelations = relations[id];
        const anyActive = commentRelations?.some(
          (relationId) => segmentEntities[relationId]?.status !== 'DELETED',
        );

        return anyActive;
      }

      return true;
    });

    if (textFilter) {
      assetCommentsFlatIds = assetCommentsFlatIds.filter((id) => {
        const comment = segmentEntities[id];
        if (comment && comment.segment_text) {
          const commentText = comment.segment_text.toLowerCase();
          return commentText.includes(textFilter.toLowerCase());
        }

        return false;
      });
    }

    return assetCommentsFlatIds;
  },
);

export const getFilteredAssetCommentIds = (
  state: AppState,
  assetId: string,
  versionId: string | undefined,
) =>
  selectAssetCommentIds(
    state,
    assetId,
    versionId,
    state.assetCommentsReducer.filterColors,
    state.assetCommentsReducer.filterText,
    state.assetCommentsReducer.versionFilter,
    state.assetCommentsReducer.sortField,
  );

export const getMentionsMode = (state: AppState): MentionsMode => {
  if (state.routeParams.route === SHARE_ASSET_PAGE_ROUTE) {
    return 'usersAndEmails';
  }
  return growthbook?.isOn(FLAG_SIMPLIFIED_ACLS)
    ? 'usersAndTeams'
    : 'usersAndGroups';
};

export const getAllowSearchUsers = (state: AppState): boolean =>
  state.routeParams.route !== SHARE_ASSET_PAGE_ROUTE ||
  Boolean(state.share.share.allow_user_search_for_mentions);
